/**
 * The simulation agent: board → circuit → solve → part behaviors → events.
 *
 * Runs at a fixed 60 Hz tick and is a pure function of (board, inputs, ticks):
 * the same actions always produce the same result, which makes it testable and
 * every recording of the flying fan identical.
 */
import { pinCells } from '../core/board';
import type { Board } from '../core/board';
import { cellKey, type PropValue } from '../core/types';
import type { SimEventKind, StampCtx } from '../parts/contract';
import { partDef } from '../parts/registry';
import { solve, volt, type Element } from './mna';

export const TICK = 1 / 60;

export interface SimEvent {
  kind: SimEventKind;
  id: string;
  type: string;
  tick: number;
  data?: Record<string, number | string | boolean>;
}

export interface Reading {
  /** Conventional current through the part, pin 0 → pin 1 (A). */
  current: number;
  /** V(pin 0) − V(pin 1). */
  voltage: number;
}

export interface Netlist {
  nodeCount: number;
  elements: Element[];
  /** Node index of every snap position ("x,y"). */
  nodeOf: Map<string, number>;
  /** Per part: its pin nodes, and how to read its current. */
  probes: Map<string, { pins: number[]; measure?: (v: (n: number) => number) => number }>;
}

type Inputs = Record<string, PropValue>;
const NO_INPUT: Inputs = Object.freeze({});

/** Build the circuit for the current board and part states. */
export function buildNetlist(
  board: Board,
  states: Map<string, object>,
  inputs: Map<string, Inputs>,
): Netlist {
  const nodeOf = new Map<string, number>();
  const parts = [...board.parts.values()].sort((a, b) => (a.id < b.id ? -1 : 1));
  for (const p of parts)
    for (const c of pinCells(p)) {
      const k = cellKey(c.x, c.y);
      if (!nodeOf.has(k)) nodeOf.set(k, nodeOf.size);
    }
  let nodeCount = nodeOf.size;
  const elements: Element[] = [];
  const probes: Netlist['probes'] = new Map();

  for (const p of parts) {
    const def = partDef(p.type);
    const pins = pinCells(p).map((c) => nodeOf.get(cellKey(c.x, c.y))!);
    const probe: { pins: number[]; measure?: (v: (n: number) => number) => number } = { pins };
    const ctx: StampCtx = {
      pin: (i) => pins[i]!,
      internal: () => nodeCount++,
      resistor: (a, b, ohms) => elements.push({ kind: 'g', a, b, g: 1 / ohms }),
      current: (a, b, amps) => elements.push({ kind: 'i', a, b, i: amps }),
      source: (pos, neg, volts, ohms) => {
        elements.push({ kind: 'g', a: pos, b: neg, g: 1 / ohms });
        // Norton: volts/ohms pushed out of "+" (flows neg → pos through the source).
        if (volts !== 0) elements.push({ kind: 'i', a: neg, b: pos, i: volts / ohms });
      },
      diode: (a, b, is, nvt) => elements.push({ kind: 'd', a, b, is, nvt }),
      measure: (fn) => (probe.measure = fn),
    };
    def.stamp(ctx, p, states.get(p.id) ?? def.initState(), inputs.get(p.id) ?? NO_INPUT);
    probes.set(p.id, probe);
  }
  return { nodeCount, elements, nodeOf, probes };
}

export class Sim {
  readonly states = new Map<string, object>();
  readonly inputs = new Map<string, Inputs>();
  readonly readings = new Map<string, Reading>();
  tick = 0;
  /** Last solve stats, for the debug overlay and tests. */
  stats = { nodes: 0, elements: 0, iterations: 0, converged: true, ms: 0 };
  private guess?: Float64Array;

  constructor(readonly board: Board) {}

  /** Transient inputs like a held button. */
  setInput(id: string, key: string, value: PropValue): void {
    this.inputs.set(id, { ...(this.inputs.get(id) ?? {}), [key]: value });
  }

  state<S extends object>(id: string): S | undefined {
    return this.states.get(id) as S | undefined;
  }

  reading(id: string): Reading {
    return this.readings.get(id) ?? { current: 0, voltage: 0 };
  }

  /** Advance one fixed tick. Returns what happened, for sound/haptics/coach/levels. */
  step(): SimEvent[] {
    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
    this.syncStates();
    const net = buildNetlist(this.board, this.states, this.inputs);
    const sol = solve(net.nodeCount, net.elements, this.guess);
    this.guess = sol.v;
    const v = (n: number) => volt(sol.v, n);

    const events: SimEvent[] = [];
    for (const p of this.board.parts.values()) {
      const probe = net.probes.get(p.id)!;
      const current = probe.measure ? probe.measure(v) : 0;
      const voltage = v(probe.pins[0] ?? -1) - v(probe.pins[1] ?? -1);
      this.readings.set(p.id, { current, voltage });
      const def = partDef(p.type);
      def.step?.({
        part: p,
        state: this.states.get(p.id)!,
        current,
        voltage,
        dt: TICK,
        input: this.inputs.get(p.id) ?? NO_INPUT,
        emit: (kind, data) => events.push({ kind, id: p.id, type: p.type, tick: this.tick, data }),
      });
    }
    this.tick++;
    this.stats = {
      nodes: net.nodeCount,
      elements: net.elements.length,
      iterations: sol.iterations,
      converged: sol.converged,
      ms: typeof performance !== 'undefined' ? performance.now() - t0 : 0,
    };
    return events;
  }

  /** Run n ticks, collecting events (handy for tests and scripted demos). */
  run(ticks: number): SimEvent[] {
    const all: SimEvent[] = [];
    for (let i = 0; i < ticks; i++) all.push(...this.step());
    return all;
  }

  private syncStates(): void {
    for (const id of this.states.keys()) if (!this.board.parts.has(id)) this.states.delete(id);
    for (const id of this.inputs.keys()) if (!this.board.parts.has(id)) this.inputs.delete(id);
    for (const id of this.readings.keys()) if (!this.board.parts.has(id)) this.readings.delete(id);
    for (const p of this.board.parts.values())
      if (!this.states.has(p.id)) this.states.set(p.id, partDef(p.type).initState());
  }
}
